import { Link } from 'react-router-dom';
import { siteInfo, editorPrinciples, testimonials, stats } from '@/mocks/site';
import usePageMeta from '@/hooks/usePageMeta';
import JsonLd from '@/components/feature/JsonLd';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumbs from '@/components/feature/Breadcrumbs';

const AboutPage = () => {
  usePageMeta({
    title: '关于我们 - 爆瓜爆料是谁 - 爆瓜爆料',
    description:
      '了解爆瓜爆料的网站定位、内容原则与发展理念。我们通过多渠道交叉核实、第一现场时间线和持续更新机制，把娱乐快讯做得更清楚、更可信。',
    keywords: '关于爆瓜爆料,内容原则,编辑团队,娱乐快讯平台',
    canonical: 'https://sibzb.cn/about',
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'AboutPage',
              name: '关于爆瓜爆料',
              url: 'https://sibzb.cn/about',
              description: siteInfo.intro,
            },
            {
              '@type': 'Organization',
              name: siteInfo.brand,
              url: 'https://sibzb.cn/',
              email: siteInfo.email,
              slogan: siteInfo.slogan,
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://sibzb.cn/' },
                { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://sibzb.cn/about' },
              ],
            },
          ],
        }}
      />
      <SiteHeader />

      <main className="flex-1">
        <div className="px-4 md:px-6 py-6 md:py-8">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
        </div>

        <section className="px-4 md:px-6" aria-labelledby="about-title">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 text-secondary-800 px-3 py-1 text-xs">
                <i className="ri-flag-2-line" />
                网站定位
              </span>
              <h1 id="about-title" className="mt-4 font-heading text-3xl md:text-4xl font-black leading-tight text-foreground-950">
                把散落全网的瓜，重新整理成看得懂的快讯
              </h1>
              <p className="mt-4 text-sm md:text-base leading-8 text-foreground-700">{siteInfo.intro}</p>
              <p className="mt-3 text-sm md:text-base leading-8 text-foreground-700">
                我们不做没有来源的猜测，也不靠夸张标题吸引点击。爆瓜爆料更在意的是：你点进来之后，能不能在几分钟内真正搞清楚发生了什么、接下来可能怎么走。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-700"
                >
                  <i className="ri-send-plane-line" />
                  联系我们
                </Link>
                <Link
                  to="/faq"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-background-200"
                >
                  查看常见问题
                </Link>
              </div>
            </div>
            <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=calm%20modern%20editorial%20newsroom%20desk%20with%20monitors%20showing%20abstract%20timeline%20charts%2C%20stylized%20illustration%2C%20green%20and%20cream%20tones%2C%20soft%20daylight%2C%20no%20text&width=1200&height=800&seq=bg-about-newsroom-01&orientation=landscape"
                alt="爆瓜爆料编辑团队的内容核实工作场景插图"
                title="爆瓜爆料编辑团队 - 内容核实"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        <section className="mt-12 bg-background-100 border-y border-background-200" aria-labelledby="positioning-title">
          <div className="px-4 md:px-6 py-10 md:py-14">
            <h2 id="positioning-title" className="font-heading text-2xl font-black text-foreground-950">
              发展理念与内容原则
            </h2>
            <p className="mt-2 text-sm leading-6 text-foreground-600 max-w-2xl">
              爆瓜爆料从一个小型的娱乐信息汇编开始，逐步形成了今天的内容流程。理念没有变过：先核实，再发布；先讲清楚，再追热度。
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {editorPrinciples.map((item) => (
                <div key={item.title} className="bg-background-50 border border-background-200 rounded-lg p-5">
                  <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 md:py-14" aria-labelledby="numbers-title">
          <h2 id="numbers-title" className="font-heading text-2xl font-black text-foreground-950">
            一些可以量化的坚持
          </h2>
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {stats.map((item) => (
              <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="w-10 h-10 rounded-md bg-secondary-100 text-secondary-800 flex items-center justify-center mb-3">
                  <i className={`${item.icon} text-lg`} />
                </span>
                <p className="font-heading text-2xl font-black text-primary-800">{item.value}</p>
                <p className="mt-1 text-xs text-foreground-600">{item.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="px-4 md:px-6 pb-14" aria-labelledby="about-voice-title">
          <h2 id="about-voice-title" className="font-heading text-2xl font-black text-foreground-950">
            用户怎么说
          </h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {testimonials.map((item) => (
              <figure key={item.name} className="bg-background-100 border border-background-200 rounded-lg p-5">
                <i className="ri-double-quotes-l text-2xl text-primary-300" />
                <blockquote className="mt-2 text-sm leading-7 text-foreground-700">{item.text}</blockquote>
                <figcaption className="mt-4 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-sm">
                    {item.name.slice(0, 1)}
                  </span>
                  <span className="text-sm text-foreground-900">{item.name}</span>
                  <span className="rounded-full bg-secondary-100 text-secondary-800 px-2 py-0.5 text-[11px]">
                    {item.tag}
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="px-4 md:px-6 pb-16" aria-labelledby="contact-band-title">
          <div className="rounded-lg bg-primary-900 text-background-50 p-6 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div>
              <h2 id="contact-band-title" className="font-heading text-2xl font-black">
                有线索、有建议、有合作？
              </h2>
              <p className="mt-2 text-sm leading-6 text-primary-100">
                投稿邮箱 {siteInfo.email}，或直接提交留言，我们都会认真看完。
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-chat-smile-2-line" />
              前往留言
            </Link>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default AboutPage;